import { useState, useRef } from "react";
import { Phase, AuthModal, User } from "./types";
import { SUSPECTS, COMMENTS_THEORY, COMMENTS_DISCUSSION } from "./data/mystery";
import { useLiveActivity } from "./hooks/useLiveActivity";
import Header from "./components/Header";
import EpisodeMeta from "./components/EpisodeMeta";
import CaseFile from "./components/CaseFile";
import SuspectCard from "./components/SuspectCard";
import ClueBlock from "./components/ClueBlock";
import AccuseSection from "./components/AccuseSection";
import RevealSection from "./components/RevealSection";
import CommentsSection from "./components/CommentsSection";
import LiveBar from "./components/LiveBar";
import LiveFeed from "./components/LiveFeed";
import AuthModalComponent from "./components/AuthModal";

const NARRATIVE =
  `11:07 PM. <strong>Ethan Kwan</strong>, a 32-year-old corporate lawyer, arrives at the townhouse of his longtime friend Adrian Cole in downtown Washington D.C. He sends a quick message to his wife confirming he's arrived.<br /><br />
  Less than 40 minutes later, Adrian's partner calls emergency services: <em>"Our friend has been stabbed. Someone broke in."</em><br /><br />
  Paramedics arrive. Ethan is dead in the guest bedroom.<br /><br />
  The house was locked from the inside. No signs of forced entry. Three precise stab wounds. Almost no blood at the scene. A knife found beside him — but it doesn't match the wounds.<br /><br />
  Three people were in the house that night.`;

const CLUE =
  `Ethan's phone shows he was alive at <strong>11:07 PM</strong>. The emergency call came at <strong>11:49 PM</strong>.<br /><br />
  Within that 42-minute window: Ethan was killed, the scene was cleaned, a false narrative was prepared, and a decoy knife was placed.<br /><br />
  One detail the detective couldn't ignore: <strong>Ethan's body shows no signs of struggle</strong> — but the fatal wounds required significant force. He never saw it coming. He trusted whoever walked in.`;

const EXPLANATION =
  `Victor's first words were the tell: <em>"There was so little blood… I knew something was wrong immediately."</em><br /><br />
  <strong>He knew what the blood level should look like — because he cleaned the scene himself.</strong> No one told him a minimal blood presence was unusual. That observation only makes sense if he had seen the original scene before it was staged.<br /><br />
  Victor also directed paramedics straight to the guest bedroom without hesitation. He didn't search. He didn't wander. He knew exactly where the body was.<br /><br />
  <strong>The 42-minute window is the second tell.</strong> Killing, cleaning, staging, and preparing a false narrative in under 42 minutes is tight for one person — but Victor had Adrian's cooperation. Adrian's role was to stay in his room, establish an alibi, and take control when police arrived.<br /><br />
  Marcus's missing kitchen knife was placed deliberately to redirect suspicion. The actual murder weapon was removed from the scene entirely.<br /><br />
  Ethan trusted Victor. He was a longtime friend of the household. He had no reason to be afraid when Victor walked into that guest room.`;

export default function App() {
  const [phase, setPhase] = useState<Phase>("reading");
  const [selectedSuspect, setSelectedSuspect] = useState<string | null>(null);
  const [bars, setBars] = useState(false);
  const [authModal, setAuthModal] = useState<AuthModal>("closed");
  const [user, setUser] = useState<User | null>(null);
  const [pendingAction, setPendingAction] = useState<"accuse" | null>(null);
  const revealRef = useRef<HTMLDivElement>(null);
  const { activeCount, events } = useLiveActivity();

  const openAuth = (action?: "accuse") => {
    if (action) setPendingAction(action);
    setAuthModal("register");
  };

  const handleAuth = (newUser: User) => {
    setUser(newUser);
    setAuthModal("closed");
    if (pendingAction === "accuse") {
      setPendingAction(null);
      runAccuse();
    }
  };

  const runAccuse = () => {
    setPhase("thinking");
    setTimeout(() => {
      setPhase("revealed");
      setTimeout(() => {
        setBars(true);
        revealRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
    }, 1800);
  };

  const handleAccuse = () => {
    if (!user) {
      openAuth("accuse");
      return;
    }
    runAccuse();
  };

  const handleLogout = () => {
    setUser(null);
    setPhase("reading");
    setSelectedSuspect(null);
    setBars(false);
  };

  return (
    <div className="app">
      <Header
        activeCount={activeCount}
        user={user}
        onAuthRequired={() => openAuth()}
        onLogout={handleLogout}
      />

      <div className="page-layout">
        {/* ── MAIN COLUMN ── */}
        <main className="main">
          <LiveBar activeCount={activeCount} />

          <EpisodeMeta episode={1} series="The Obsidian Club Murders" />

          <h1 className="mystery-title">The Guest Room</h1>
          <p className="mystery-tagline">
            A quiet night. A trusted home. A death that shouldn't have been possible.
          </p>

          <CaseFile timestamp="11:07 PM." narrative={NARRATIVE} />

          <div className="section-label">— Three Suspects</div>
          <div className="suspects">
            {SUSPECTS.map((s) => (
              <SuspectCard
                key={s.id}
                suspect={s}
                phase={phase}
                selectedSuspect={selectedSuspect}
                onSelect={setSelectedSuspect}
              />
            ))}
          </div>

          <ClueBlock clue={CLUE} />

          {phase === "reading" && (
            <AccuseSection
              suspects={SUSPECTS}
              selectedSuspect={selectedSuspect}
              user={user}
              onAccuse={handleAccuse}
              onAuthRequired={() => openAuth("accuse")}
            />
          )}

          {phase === "thinking" && (
            <div className="thinking">Reviewing the evidence...</div>
          )}

          {phase === "revealed" && (
            <>
              <RevealSection
                ref={revealRef}
                suspects={SUSPECTS}
                selectedSuspect={selectedSuspect}
                bars={bars}
                explanation={EXPLANATION}
              episodeTitle="The Guest Room"
              />
              <div className="next-ep">
                <div>
                  <div className="next-ep-label">Next · Episode 02</div>
                  <div className="next-ep-title">The Night Shift</div>
                </div>
                <span className="next-arrow">→</span>
              </div>
            </>
          )}

          <CommentsSection
            phase={phase}
            user={user}
            theoryComments={COMMENTS_THEORY}
            discussionComments={COMMENTS_DISCUSSION}
            onAuthRequired={() => openAuth()}
          />
        </main>

        {/* ── SIDEBAR ── */}
        <aside className="sidebar">
          <LiveFeed events={events} />
        </aside>
      </div>

      {/* ── AUTH MODAL ── */}
      <AuthModalComponent
        mode={authModal}
        onClose={() => { setAuthModal("closed"); setPendingAction(null); }}
        onAuth={handleAuth}
        switchMode={setAuthModal}
      />
    </div>
  );
}
